import { useParams, Navigate, Link } from 'react-router-dom';
import { getServiceBySlug } from '@/lib/serviceData';
import { SERVICE_MENU, TESTIMONIALS } from '@/lib/constants';
import { usePopup } from '@/lib/PopupContext';
import { useMeta } from '@/hooks/useMeta';
import PricingSection from '@/components/shared/PricingSection';
import ProcessSection from '@/components/shared/ProcessSection';
import FAQSection from '@/components/shared/FAQSection';
import TestimonialSlider from '@/components/shared/TestimonialSlider';
import CTABanner from '@/components/shared/CTABanner';
import { Check, ArrowRight, ShieldCheck, Clock, Users, Headphones, Quote, ChevronRight } from 'lucide-react';

const DIFFERENTIATORS = [
  { icon: ShieldCheck, title: 'Expert-Led Process', desc: 'Every case is handled by experienced IP specialists who understand the nuances of your industry.' },
  { icon: Clock, title: 'Fast Turnaround', desc: 'We prioritize efficiency to get your application filed quickly without sacrificing quality or accuracy.' },
  { icon: Users, title: 'Dedicated Case Manager', desc: 'A single point of contact who knows your case inside and out, providing personalized updates.' },
  { icon: Headphones, title: 'Ongoing Support', desc: 'Our relationship doesn\'t end at filing. We provide continued guidance through approval and beyond.' },
];

export default function ServicePage() {
  const { slug } = useParams();
  const { openPopup } = usePopup();
  const service = getServiceBySlug(slug);

  useMeta({
    title: service ? `${service.title} | ${service.category} | AmericanTrademark Firm` : 'Service | AmericanTrademark Firm',
    description: service ? service.heroDesc : 'Professional IP services from AmericanTrademark Firm.',
    keywords: service ? `${service.title.toLowerCase()}, ${service.category.toLowerCase()}, AmericanTrademark Firm, USA` : 'trademark services',
  });

  if (!service) {
    return <Navigate to="/" replace />;
  }

  // Find related services from the same category
  const currentCategory = SERVICE_MENU.find(cat =>
    cat.services.some(s => s.slug === slug)
  );
  const relatedServices = currentCategory
    ? currentCategory.services.filter(s => s.slug !== slug).slice(0, 4)
    : [];

  return (
    <div data-testid="service-page">
      {/* 1. Hero Banner with Background Image */}
      <section className="relative py-24 md:py-36 overflow-hidden" data-testid="service-hero">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${service.heroImage || service.image})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#09090B]/80 via-[#09090B]/65 to-[#09090B]/40" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#6B9FFF] mb-4">{service.category}</p>
            <h1 className="font-heading font-black text-5xl md:text-6xl tracking-tighter leading-none text-white mb-6">
              {service.title}
            </h1>
            <p className="text-lg md:text-xl text-zinc-300 font-body leading-relaxed mb-8">
              {service.heroDesc}
            </p>
            <button
              onClick={() => openPopup()}
              className="bg-[#002FA7] text-white hover:bg-[#002480] transition-colors px-8 py-4 font-bold text-sm tracking-widest uppercase inline-flex items-center gap-2"
              data-testid="service-hero-cta"
            >
              Get Started <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* 2. Info Section with Image */}
      <section className="py-24 md:py-32 bg-white" data-testid="service-info">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src={service.image}
                alt={service.title}
                className="w-full h-[400px] object-cover"
                loading="lazy"
              />
            </div>
            <div>
              <h2 className="font-heading font-extrabold text-4xl md:text-5xl tracking-tight text-[#09090B] leading-tight mb-6">
                About This Service
              </h2>
              <p className="text-base md:text-lg text-zinc-600 font-body leading-relaxed mb-6">
                {service.content}
              </p>
              <button
                onClick={() => openPopup()}
                className="bg-[#002FA7] text-white hover:bg-[#002480] transition-colors px-8 py-4 font-bold text-sm tracking-widest uppercase"
                data-testid="service-info-cta"
              >
                Register My Trademark
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Benefits */}
      <section className="py-24 md:py-32 bg-[#F4F4F5]" data-testid="service-benefits">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-3">Benefits</p>
            <h2 className="font-heading font-extrabold text-4xl md:text-5xl tracking-tight text-[#09090B] leading-tight">
              Why Choose This Service
            </h2>
          </div>
          <div className="mobile-slider md:grid-cols-2 lg:grid-cols-3 gap-0">
            {service.benefits.map((b, idx) => (
              <div key={idx} className="border border-[#E4E4E7] bg-white p-8" data-testid={`benefit-${idx}`}>
                <Check className="w-6 h-6 text-[#002FA7] mb-3" />
                <h3 className="font-heading font-bold text-lg text-[#09090B] mb-2">{b.title}</h3>
                <p className="text-sm text-zinc-600 font-body leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Why Choose Us / Differentiators - NEW */}
      <section className="py-24 md:py-32 bg-white" data-testid="service-differentiators">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-3">Why AmericanTrademark Firm</p>
              <h2 className="font-heading font-extrabold text-4xl md:text-5xl tracking-tight text-[#09090B] leading-tight mb-6">
                The Advantage of Working With Us
              </h2>
              <p className="text-base text-zinc-600 font-body leading-relaxed mb-8">
                We combine deep industry expertise with a client-first approach. When you choose AmericanTrademark Firm for {service.title.toLowerCase()}, you're partnering with a team that's invested in your success.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {DIFFERENTIATORS.map((d, idx) => {
                  const Icon = d.icon;
                  return (
                    <div key={idx} data-testid={`differentiator-${idx}`}>
                      <Icon className="w-6 h-6 text-[#002FA7] mb-2" />
                      <h4 className="font-heading font-bold text-sm text-[#09090B] mb-1">{d.title}</h4>
                      <p className="text-xs text-zinc-500 font-body leading-relaxed">{d.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1758873272809-7947b9a73fe5?w=800&q=80"
                alt="Colleagues collaborating"
                className="w-full h-[420px] object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 5. Pricing (only for trademark registration) */}
      {service.showPricing && <PricingSection />}

      {/* 6. Process Section */}
      <ProcessSection />

      {/* 7. Testimonials - NEW */}
      <TestimonialSlider />

      {/* 8. FAQ */}
      {service.faq && service.faq.length > 0 && (
        <FAQSection items={service.faq} title="Common Questions" />
      )}

      {/* 9. Related Services - NEW */}
      {relatedServices.length > 0 && (
        <section className="py-24 md:py-32 bg-white" data-testid="related-services">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-center mb-16">
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-3">Related Services</p>
              <h2 className="font-heading font-extrabold text-4xl md:text-5xl tracking-tight text-[#09090B] leading-tight">
                Explore More {currentCategory?.category}
              </h2>
            </div>
            <div className="mobile-slider md:grid-cols-2 lg:grid-cols-4 gap-0">
              {relatedServices.map((rs, idx) => (
                <Link
                  key={rs.slug}
                  to={`/services/${rs.slug}`}
                  className="service-card border border-[#E4E4E7] p-6 group block"
                  data-testid={`related-service-${idx}`}
                >
                  <h3 className="font-heading font-bold text-base text-[#09090B] mb-3 group-hover:text-[#002FA7] transition-colors">
                    {rs.title}
                  </h3>
                  <span className="inline-flex items-center gap-1 text-sm font-bold text-[#002FA7] group-hover:gap-2 transition-all">
                    Learn More <ChevronRight className="w-4 h-4" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 10. CTA Banner */}
      <CTABanner
        title={`Ready for ${service.title}?`}
        subtitle="Take the first step today. Our expert team is ready to guide you through the entire process."
      />
    </div>
  );
}
